import { findAllBuyableSources } from './effectHelpers';
import { performBuy } from '../actions/villageActions';

// Every printed "Buy/Place 1 <kind of card>" ability - Bounty Hunter, Tough,
// Rapparee, Stealthy, Chalice, Armsman, Clan Champion, Isri Bladespinner,
// Gray Ooze and friends - comes down to "pick one stack, then performBuy from
// it." They were all written out longhand, and every one of them picked the
// stack itself:
//
//   - one family did `choices?.villageDefId ?? keys[0]` - it accepted a
//     choice, but nothing ever supplied one, so "the first matching stack"
//     was the behavior every time;
//   - the other called findCheapestBuyableSource, which takes the cheapest
//     match and has no choice parameter at all.
//
// Matt reported both, and asked for the whole class:
//
//   "The clan champion spoils ability didn't let me choose what weapon to
//   buy. This seems like a widespread bug of the game choosing things for me.
//   We should root out these mistakes and fix them all."
//
//   "I just tried to use the Isri Bladespinner's spoils ability and the game
//   just chose a weapon for me. I should have been able to choose myself what
//   weapon I wanted."
//
// The gap was never in any individual handler - it was that a stack is not a
// card in your hand, so the dock's two existing choice mechanisms (inline
// hand-highlighting via getCandidates, and the pendingHandChoices queue) had
// nothing to attach to, and each new "Buy 1 X" card just quietly auto-picked.
// Trader was the sole exception, with a hand-rolled picker in PlayerDock.
//
// Declaring the candidate list once per card, next to its own resolve, means
// both halves read the same list: `resolve` still auto-picks when no choice
// is supplied (the AI, replays, any non-prompting caller), while
// `getBuyableSources` exposes exactly the same list so the dock can show a
// real picker. A new "Buy 1 X" card gets the picker for free.

// Sources are {type: 'village'|'basic', key, cost} - the same shape
// performBuy takes, and the same shape Trader's own picker already builds.
// Basic stacks matter here: a Longspear is a weapon like any other, so
// "buy 1 weapon" has to be able to offer it.
export function buyableSourcesFor(state, { matches, includeBasics = true }) {
  const all = findAllBuyableSources(state, matches || (() => true));
  return includeBasics ? all : all.filter((s) => s.type === 'village');
}

// Builds a registry entry ({ getBuyableSources, resolve }) for a "buy/place 1
// card" ability.
//
// `matches` filters stacks by their top card's definition (omit for "any
// card"). `onEmpty` decides what a card with no legal target does: 'throw'
// (the ability was offered but can't be honored - what these handlers already
// did) or 'skip' (a bonus rider on something that already happened, so it
// no-ops - Chalice's own existing behavior). `buyOptions` passes each card's
// own gold-check and destination rules straight through to performBuy.
export function buyOneCard({
  matches, errorMessage, onEmpty = 'throw', buyOptions = { requireGoldCheck: false }, includeBasics = true,
}) {
  const getBuyableSources = (state) => buyableSourcesFor(state, { matches, includeBasics });
  return {
    getBuyableSources,
    resolve: (state, { playerId, choices }) => {
      const legal = getBuyableSources(state);
      // choices.source is what the picker supplies; choices.villageDefId is
      // the older village-stack-only spelling several handlers (and their
      // tests) already used - both accepted, and both validated against
      // what's actually legal right now, so a stale pick (the stack sold out
      // while the picker was open) falls back rather than buying something
      // the card never allowed.
      const picked = choices?.source
        ? legal.find((s) => s.type === choices.source.type && s.key === choices.source.key)
        : legal.find((s) => s.type === 'village' && s.key === choices?.villageDefId);
      const source = picked || legal[0];
      if (!source) {
        if (onEmpty === 'skip') return state;
        throw new Error(errorMessage);
      }
      return performBuy(state, playerId, { type: source.type, key: source.key }, buyOptions);
    },
  };
}
